<template>
    <div class="min-h-screen">
        <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
            <transition name="view-fade" mode="out-in">
                <div v-if="currentView === 'list'" key="list">
                    <header class="mb-8">
                        <h1
                            class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100 text-center sm:text-left">
                            Orçamentos de <span class="text-brand-600 dark:text-brand-500">Projetos</span> </h1>
                        <p class="text-sm text-slate-600 dark:text-slate-400 text-center sm:text-left mt-1"> Planeje e
                            acompanhe os gastos dos seus projetos em grupo. </p>
                    </header>
                    <div class="mb-8 flex justify-start">
                        <button @click="handleNovoOrcamento"
                            class="btn-primary flex items-center justify-center text-sm shadow-lg hover:shadow-brand-700/50 transition-all">
                            <span class="material-symbols-outlined mr-2 text-lg">edit_document</span> Criar Novo
                            Orçamento </button>
                    </div>
                    <div v-if="convitesPendentes.length > 0" class="mb-12">
                        <h2
                            class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4 border-b pb-2 border-slate-200 dark:border-slate-700">
                            Convites Pendentes </h2>
                        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div v-for="convite in convitesPendentes" :key="convite.conviteId"
                                class="bg-white dark:bg-brand-800 p-5 rounded-2xl shadow-lg border-2 border-brand-500 animate-pulse-fast">
                                <p class="text-sm text-slate-600 dark:text-slate-400"> <strong
                                        class="text-slate-800 dark:text-slate-200">{{ convite.nomeConvidadoPor
                                        }}</strong> convidou você para: </p>
                                <h4 class="text-lg font-bold text-brand-600 dark:text-brand-500 mt-1 truncate">{{
                                    convite.nomeEntidade }}</h4>
                                <div class="flex items-center justify-end gap-3 mt-4">
                                    <button @click="handleDecline(convite.conviteId)"
                                        class="btn-danger-outline !text-xs !py-1.5 !px-3">Recusar</button>
                                    <button @click="handleAccept(convite.conviteId)"
                                        class="btn-primary !text-xs !py-1.5 !px-3">Aceitar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="isLoading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="i in 3" :key="i" class="skeleton-card">
                            <div class="h-5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded"></div>
                            <div class="h-3 w-1/2 bg-slate-200 dark:bg-slate-700 rounded mt-2"></div>
                            <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <div class="h-2.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full"></div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="orcamentos.length === 0 && convitesPendentes.length === 0" class="empty-state">
                        <span
                            class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">folder_off</span>
                        <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum orçamento de projeto
                            encontrado.</p>
                        <p class="text-sm">Crie um novo orçamento ou aceite um convite para começar.</p>
                    </div>
                    <transition-group v-else-if="orcamentos.length > 0" name="list" tag="div"
                        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div v-for="orcamento in orcamentos" :key="orcamento.uuid" @click="selectOrcamento(orcamento)"
                            class="card-budget group">
                            <div>
                                <h3
                                    class="font-bold text-xl text-slate-800 dark:text-slate-100 truncate group-hover:text-brand-600 dark:group-hover:text-brand-500 transition-colors">
                                    {{ orcamento.nome }} </h3>
                                <p class="text-sm text-slate-500 dark:text-slate-400 mt-1 h-10 overflow-hidden">{{
                                    orcamento.descricao }}</p>
                                <div class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <div class="flex justify-between items-baseline mb-2"> <span
                                            class="text-sm font-medium text-slate-500 dark:text-slate-400">Progresso</span>
                                        <span class="text-sm font-bold"
                                            :class="getUsageColor(orcamento.valorGasto, orcamento.valorOrcado)"> {{
                                                orcamento.valorOrcado > 0 ? Math.round((orcamento.valorGasto /
                                                    orcamento.valorOrcado) * 100) : 0 }}% </span>
                                    </div>
                                    <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
                                        <div class="h-2.5 rounded-full transition-all duration-500"
                                            :class="getUsageBgColor(orcamento.valorGasto, orcamento.valorOrcado)"
                                            :style="{ width: `${orcamento.valorOrcado > 0 ? Math.min((orcamento.valorGasto / orcamento.valorOrcado) * 100, 100) : 0}%` }">
                                        </div>
                                    </div>
                                    <div class="flex justify-between text-xs mt-1.5 text-slate-500 dark:text-slate-400">
                                        <span>{{ displayCurrency(orcamento.valorGasto) }}</span> <span>{{
                                            displayCurrency(orcamento.valorOrcado) }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition-group>

                </div>

                <div v-else-if="currentView === 'details' && selectedOrcamento" key="details">
                    <button @click="returnToList"
                        class="mb-4 flex items-center text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-500/80 transition-colors">
                        <span class="material-symbols-outlined mr-1">arrow_back</span>Voltar para a Lista
                    </button>

                    <header class="bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg mb-8">
                        <div class="md:flex justify-between items-start">
                            <div>
                                <h1 class="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100">{{
                                    selectedOrcamento.nome }}
                                </h1>
                                <p class="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">{{
                                    selectedOrcamento.descricao }}
                                </p>
                            </div>
                            <div class="flex flex-wrap gap-3 mt-4 md:mt-0 flex-shrink-0">
                                <button @click="handleAddGastoClick" class="btn-primary flex-1 sm:flex-none">
                                    <span class="material-symbols-outlined mr-2 text-lg">add</span> Gasto
                                </button>
                                <button v-if="isOwner" @click="showInviteModal = true"
                                    class="btn-secondary flex-1 sm:flex-none">
                                    <span class="material-symbols-outlined mr-2 text-lg">person_add</span> Convidar
                                </button>
                                <button @click="handleEditOrcamento" class="btn-secondary flex-1 sm:flex-none">
                                    <span class="material-symbols-outlined mr-2 text-lg">edit</span> Editar
                                </button>
                            </div>
                        </div>
                        <div class="mt-6">
                            <div class="flex justify-between items-center mb-1 text-slate-600 dark:text-slate-300"><span
                                    class="font-medium">Orçamento Utilizado</span><span class="font-bold">{{
                                        selectedOrcamento.valorOrcado > 0 ? Math.round((selectedOrcamento.valorGasto /
                                            selectedOrcamento.valorOrcado) * 100) : 0 }}%</span></div>
                            <div class="w-full bg-slate-200 dark:bg-brand-900/70 rounded-full h-4 mb-2">
                                <div class="bg-gradient-to-r from-green-400 to-sky-400 h-4 rounded-full"
                                    :style="{ width: `${selectedOrcamento.valorOrcado > 0 ? Math.min((selectedOrcamento.valorGasto / selectedOrcamento.valorOrcado) * 100, 100) : 0}%` }">
                                </div>
                            </div>
                            <div class="flex justify-between items-baseline"><span
                                    class="text-2xl font-bold text-slate-800 dark:text-white">{{
                                        displayCurrency(selectedOrcamento.valorGasto) }}</span><span
                                    class="text-sm text-slate-500 dark:text-slate-400">de {{
                                        displayCurrency(selectedOrcamento.valorOrcado) }}</span></div>
                        </div>
                    </header>

                    <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Gastos Registrados</h2>
                    <div v-if="!selectedOrcamento.gastos || selectedOrcamento.gastos.length === 0" class="empty-state">
                        <span
                            class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">receipt_long</span>
                        <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum gasto registrado.</p>
                    </div>
                    <div v-else class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg">
                        <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                            <li v-for="gasto in selectedOrcamento.gastos" :key="gasto.id"
                                class="p-4 flex items-center justify-between group">
                                <div class="flex items-center">
                                    <div
                                        class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700 text-brand-600 dark:text-brand-500 flex items-center justify-center flex-shrink-0">
                                        <span class="material-symbols-outlined">{{ getIconForGasto(gasto) }}</span>
                                    </div>
                                    <div class="ml-4">
                                        <p class="font-semibold text-slate-800 dark:text-slate-100">{{ gasto.descricao
                                            }} <span v-if="gasto.categoria"
                                                class="text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 px-2 py-0.5 rounded-full ml-2">{{
                                                    gasto.categoria }}</span> </p>
                                        <p class="text-sm text-slate-500 dark:text-slate-400">{{
                                            displayCurrency(gasto.valor) }} em {{ formatDate(gasto.data) }} por {{
                                                gasto.registradoPorNome }}</p>
                                    </div>
                                </div>
                                <div
                                    class="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button v-if="isOwner || gasto.registradoPorId === currentUser.id"
                                        @click.stop="editGasto(gasto)"
                                        class="btn-action-icon text-sky-500 dark:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-600"
                                        title="Editar Gasto">
                                        <span class="material-symbols-outlined text-lg">edit</span>
                                    </button>

                                    <button v-if="isOwner || gasto.registradoPorId === currentUser.id"
                                        @click="confirmDeleteGasto(gasto)"
                                        class="btn-action-icon text-red-500 dark:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-600"
                                        title="Remover Gasto"><span
                                            class="material-symbols-outlined text-lg">delete</span>
                                    </button>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-12">
                        <div>
                            <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Membros</h2>
                            <div v-if="!selectedOrcamento.membros || selectedOrcamento.membros.length === 0"
                                class="empty-state h-full flex-col justify-center"> <span
                                    class="material-symbols-outlined text-6xl text-brand-400 dark:text-brand-700">group_off</span>
                                <p class="mt-4 font-medium text-slate-700 dark:text-slate-300">Nenhum membro encontrado.
                                </p>
                            </div>
                            <div v-else class="bg-white dark:bg-brand-800 rounded-2xl shadow-lg">
                                <ul class="divide-y divide-slate-200 dark:divide-slate-700">
                                    <li v-for="membro in selectedOrcamento.membros" :key="membro.id"
                                        class="p-4 flex items-center justify-between transition-opacity"
                                        :class="{ 'opacity-50': membro.status === 'PENDENTE' }">
                                        <div class="flex items-center">
                                            <div
                                                class="w-10 h-10 rounded-full bg-brand-100 dark:bg-brand-700 text-brand-600 dark:text-brand-500 flex items-center justify-center text-sm font-bold ring-2 ring-white dark:ring-slate-800 flex-shrink-0">
                                                {{ membro.nome.charAt(0).toUpperCase() }}</div>
                                            <div class="ml-4">
                                                <p
                                                    class="font-semibold text-slate-800 dark:text-slate-100 flex items-center">
                                                    {{ membro.nome }} <span
                                                        v-if="membro.id === selectedOrcamento.criadorId"
                                                        class="tag-owner">Dono</span> <span
                                                        v-if="membro.status === 'PENDENTE'"
                                                        class="tag-pending">Pendente</span> </p>
                                                <p class="text-sm text-slate-500 dark:text-slate-400">{{ membro.email }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="flex items-center"> <button
                                                v-if="isOwner && membro.id !== currentUser.id && membro.status === 'ATIVO'"
                                                @click="confirmRemoveMember(membro)"
                                                class="btn-danger-outline !p-2 !rounded-full"
                                                title="Remover membro"><span
                                                    class="material-symbols-outlined text-lg">person_remove</span></button>
                                            <button
                                                v-if="isOwner && membro.id !== currentUser.id && membro.status === 'PENDENTE'"
                                                @click="confirmCancelInvite(membro)"
                                                class="btn-secondary !p-2 !rounded-full" title="Cancelar convite"><span
                                                    class="material-symbols-outlined text-lg">cancel</span></button>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div>
                            <h2 class="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">Zona de Perigo</h2>
                            <div
                                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 p-5 rounded-2xl flex flex-col items-start gap-4">
                                <div v-if="isOwner">
                                    <h4 class="font-bold text-red-800 dark:text-red-300">Excluir Projeto</h4>
                                    <p class="text-sm text-red-600 dark:text-red-300/80 mb-2">Esta ação é irreversível e
                                        excluirá todos os gastos e membros.</p> <button @click="confirmDeleteOrcamento"
                                        class="btn-danger-outline flex items-center text-sm"> <span
                                            class="material-symbols-outlined mr-2 text-lg">delete_forever</span> Excluir
                                        Orçamento </button>
                                </div>
                                <div v-if="!isOwner">
                                    <h4 class="font-bold text-red-800 dark:text-red-300">Sair do Projeto</h4>
                                    <p class="text-sm text-red-600 dark:text-red-300/80 mb-2">Você perderá o acesso a
                                        este orçamento e seus dados.</p> <button @click="confirmLeaveOrcamento"
                                        class="btn-danger-outline flex items-center text-sm"> <span
                                            class="material-symbols-outlined mr-2 text-lg">logout</span> Sair do Projeto
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showCreateModal" @click.self="showCreateModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Criar Orçamento de Projeto</h3><button @click="showCreateModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleCreateOrcamento" class="p-6 space-y-4">
                            <div><label class="form-label">Nome do Projeto</label><input v-model="newOrcamento.nome"
                                    type="text" required placeholder="Ex: Reforma da Cozinha" class="form-input" />
                            </div>
                            <div><label class="form-label">Descrição</label><input v-model="newOrcamento.descricao"
                                    type="text" placeholder="Objetivos do projeto" class="form-input" /></div>
                            <div><label class="form-label">Orçamento Total (R$)</label><input
                                    v-model.number="newOrcamento.valorOrcado" type="number" step="0.01" required
                                    placeholder="15000.00" class="form-input" /></div>
                            <div><label class="form-label">Convidar Membros (opcional)</label><textarea
                                    v-model="newOrcamento.emailsMembros" rows="3"
                                    placeholder="Separe os emails por vírgula ou espaço" class="form-input"></textarea>
                            </div>
                            <footer class="modal-footer"><button @click="showCreateModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmitting" class="btn-primary flex items-center"><span
                                        v-if="isSubmitting"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmitting ? 'Criando...' : 'Criar Orçamento' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showEditOrcamentoModal" @click.self="showEditOrcamentoModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Editar Orçamento</h3><button @click="showEditOrcamentoModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleUpdateOrcamento" class="p-6 space-y-4">
                            <div><label class="form-label">Nome do Projeto</label><input
                                    v-model="editOrcamentoForm.nome" type="text" required class="form-input"></div>
                            <div><label class="form-label">Descrição</label><input v-model="editOrcamentoForm.descricao"
                                    type="text" class="form-input"></div>
                            <div><label class="form-label">Orçamento Total (R$)</label><input
                                    v-model.number="editOrcamentoForm.valorOrcado" type="number" step="0.01" required
                                    class="form-input"></div>
                            <footer class="modal-footer"><button @click="showEditOrcamentoModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmitting" class="btn-primary flex items-center"><span
                                        v-if="isSubmitting"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmitting ? 'Salvando...' : 'Salvar' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showAddGastoModal" @click.self="showAddGastoModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Adicionar Gasto</h3><button @click="showAddGastoModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleAddGasto" class="p-6 space-y-4">
                            <div><label class="form-label">Descrição do Gasto</label><input
                                    v-model="gastoForm.descricao" type="text" required
                                    placeholder="Ex: Cimento, Pisos, etc." class="form-input" /></div>
                            <div><label class="form-label">Valor (R$)</label><input v-model.number="gastoForm.valor"
                                    type="number" step="0.01" required placeholder="500.00" class="form-input" /></div>
                            <div><label class="form-label">Categoria</label><input v-model="gastoForm.categoria"
                                    type="text" placeholder="Ex: Material, Mão de Obra" class="form-input" /></div>
                            <div><label class="form-label">Data</label><input v-model="gastoForm.data" type="date"
                                    required class="form-input" /></div>
                            <footer class="modal-footer"><button @click="showAddGastoModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmitting" class="btn-primary flex items-center"><span
                                        v-if="isSubmitting"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmitting ? 'Adicionando...' : 'Adicionar' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>

            <transition name="modal-fade">
                <div v-if="showEditGastoModal" @click.self="showEditGastoModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Editar Gasto</h3><button @click="showEditGastoModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleUpdateGasto" class="p-6 space-y-4">
                            <div><label class="form-label">Descrição do Gasto</label><input
                                    v-model="editGastoForm.descricao" type="text" required class="form-input" /></div>
                            <div><label class="form-label">Valor (R$)</label><input v-model.number="editGastoForm.valor"
                                    type="number" step="0.01" required class="form-input" /></div>
                            <div><label class="form-label">Categoria</label><input v-model="editGastoForm.categoria"
                                    type="text" class="form-input" /></div>
                            <div><label class="form-label">Data</label><input v-model="editGastoForm.data" type="date"
                                    required class="form-input" /></div>
                            <footer class="modal-footer"><button @click="showEditGastoModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmitting" class="btn-primary flex items-center"><span
                                        v-if="isSubmitting"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmitting ? 'Salvando...' : 'Salvar' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showInviteModal" @click.self="showInviteModal = false" class="modal-backdrop">
                    <div class="modal-container">
                        <header class="modal-header">
                            <h3>Convidar Membro</h3><button @click="showInviteModal = false"
                                class="modal-close-btn"><span class="material-symbols-outlined">close</span></button>
                        </header>
                        <form @submit.prevent="handleInviteMember" class="p-6 space-y-4">
                            <div><label class="form-label">Email do Convidado</label><input v-model="inviteForm.email"
                                    type="email" required placeholder="nome@exemplo.com" class="form-input" /></div>
                            <footer class="modal-footer"><button @click="showInviteModal = false" type="button"
                                    class="btn-secondary">Cancelar</button><button type="submit"
                                    :disabled="isSubmitting" class="btn-primary flex items-center"><span
                                        v-if="isSubmitting"
                                        class="animate-spin material-symbols-outlined mr-2">progress_activity</span>{{
                                            isSubmitting ? 'Convidando...' : 'Convidar' }}</button></footer>
                        </form>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmDeleteGasto" @click.self="showConfirmDeleteGasto = false" class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-red-400 mx-auto">warning</span>
                            <h3 class="modal-title">Excluir Gasto?</h3>
                            <p class="modal-text">Tem certeza que deseja excluir o gasto "{{ gastoToDelete?.descricao
                                }}"?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmDeleteGasto = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeDeleteGasto"
                                class="btn-danger">Sim, Excluir</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmDeleteOrcamento" @click.self="showConfirmDeleteOrcamento = false"
                    class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-red-400 mx-auto">delete_forever</span>
                            <h3 class="modal-title">Excluir Orçamento?</h3>
                            <p class="modal-text">Esta ação excluirá permanentemente o orçamento e todos os seus gastos.
                            </p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmDeleteOrcamento = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeDeleteOrcamento"
                                class="btn-danger">Sim, Excluir</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmRemoveMember" @click.self="showConfirmRemoveMember = false"
                    class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-amber-400 mx-auto">person_remove</span>
                            <h3 class="modal-title">Remover Membro?</h3>
                            <p class="modal-text">Tem certeza que deseja remover <strong>{{ memberToRemove?.nome
                                    }}</strong> do projeto?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmRemoveMember = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeRemoveMember"
                                class="btn-danger">Sim, Remover</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmCancelInvite" @click.self="showConfirmCancelInvite = false"
                    class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-amber-400 mx-auto">cancel</span>
                            <h3 class="modal-title">Cancelar Convite?</h3>
                            <p class="modal-text">Tem certeza que deseja cancelar o convite enviado para <strong>{{
                                inviteToCancel?.nome }}</strong>?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmCancelInvite = false" type="button"
                                class="btn-secondary">Voltar</button><button @click="executeCancelInvite"
                                class="btn-danger">Sim, Cancelar</button></div>
                    </div>
                </div>
            </transition>
            <transition name="modal-fade">
                <div v-if="showConfirmLeave" @click.self="showConfirmLeave = false" class="modal-backdrop">
                    <div class="modal-container max-w-md p-0">
                        <div class="p-6 text-center"><span
                                class="material-symbols-outlined text-5xl text-amber-400 mx-auto">logout</span>
                            <h3 class="modal-title">Sair do Projeto?</h3>
                            <p class="modal-text">Tem certeza que deseja sair do projeto <strong>"{{
                                selectedOrcamento?.nome }}"</strong>?</p>
                        </div>
                        <div class="modal-footer"><button @click="showConfirmLeave = false" type="button"
                                class="btn-secondary">Cancelar</button><button @click="executeLeaveOrcamento"
                                class="btn-danger">Sim, Sair</button></div>
                    </div>
                </div>
            </transition>

            <transition name="notification">
                <div v-if="notification.show" class="hidden md:block fixed top-5 right-5 w-full max-w-sm z-[70]">
                    <div class="p-4 rounded-lg shadow-2xl flex items-start text-white"
                        :class="notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'">
                        <div class="flex-shrink-0"><span class="material-symbols-outlined">{{ notification.type ===
                            'success' ? 'check_circle' : 'error' }}</span></div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm font-bold">{{ notification.message }}</p>
                        </div>
                        <div class="ml-auto pl-3"><button @click="notification.show = false"
                                class="-mx-1.5 -my-1.5 p-1.5 rounded-md hover:bg-black/20 focus:outline-none focus:ring-2 focus:ring-white"><span
                                    class="material-symbols-outlined text-lg">close</span></button></div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';

const route = useRoute();
const router = useRouter();
const orcamentos = ref([]);
const convitesPendentes = ref([]);
const selectedOrcamento = ref(null);
const currentUser = ref({ id: null });
const currentView = ref('list');
const isLoading = ref(true);
const isSubmitting = ref(false);

const isMobile = ref(false);
const checkDevice = () => {
    isMobile.value = window.innerWidth < 768;
};

const showCreateModal = ref(false);
const showAddGastoModal = ref(false);
const showEditGastoModal = ref(false);
const showInviteModal = ref(false);
const showEditOrcamentoModal = ref(false);
const showConfirmDeleteGasto = ref(false);
const showConfirmDeleteOrcamento = ref(false);
const showConfirmRemoveMember = ref(false);
const showConfirmLeave = ref(false);
const showConfirmCancelInvite = ref(false);

const gastoToDelete = ref(null);
const gastoToEdit = ref(null);
const memberToRemove = ref(null);
const inviteToCancel = ref(null);
const newOrcamento = ref({ nome: '', descricao: '', valorOrcado: null, emailsMembros: '' });
const gastoForm = ref({ descricao: '', valor: null, categoria: '', data: new Date().toISOString().split('T')[0] });
const editGastoForm = ref({ id: null, descricao: '', valor: null, categoria: '', data: '' });
const inviteForm = ref({ email: '' });
const editOrcamentoForm = ref({ nome: '', descricao: '', valorOrcado: null });

const notification = ref({ show: false, type: 'success', message: '' });
const notificationTimer = ref(null);
const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const isOwner = computed(() => (currentUser.value?.id && selectedOrcamento.value?.criadorId) ? currentUser.value.id === selectedOrcamento.value.criadorId : false);

const handleNovoOrcamento = () => {
    if (isMobile.value) {
        router.push({ name: 'OrcamentoProjetoAdd' });
    } else {
        openCreateOrcamentoModal();
    }
};

const editGasto = (gasto) => {
    if (!selectedOrcamento.value) return
    if (isMobile.value) {
        router.push({
            name: 'OrcamentoProjetoGastoEdit',
            params: { orcamentoUuid: selectedOrcamento.value.uuid, gastoId: gasto.id }
        })
    } else {
        openEditGastoModal(gasto)
    }
}

const handleEditOrcamento = () => {
    if (!selectedOrcamento.value) return;
    if (isMobile.value) {
        router.push({ name: 'OrcamentoProjetoEdit', params: { uuid: selectedOrcamento.value.uuid } });
    } else {
        openEditOrcamentoModal();
    }
};

const handleAddGastoClick = () => {
    if (!selectedOrcamento.value) return;
    if (isMobile.value) {
        router.push({ name: 'OrcamentoProjetoGastoAdd', params: { orcamentoUuid: selectedOrcamento.value.uuid } });
    } else {
        openAddGastoModal();
    }
};


const showNotification = (type, message, duration = 4000) => {
    if (notificationTimer.value) clearTimeout(notificationTimer.value);
    notification.value = { show: true, type, message };
    notificationTimer.value = setTimeout(() => { notification.value.show = false; }, duration);
};
const displayCurrency = (value) => value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00';
const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    } catch { return dateString; }
};
const getUsageColor = (gasto, orcado) => {
    if (!orcado || orcado === 0) return 'text-slate-500';
    const percentage = (gasto / orcado) * 100;
    if (percentage > 100) return 'text-red-600 dark:text-red-400';
    if (percentage > 80) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
};
const getUsageBgColor = (gasto, orcado) => {
    if (!orcado || orcado === 0) return 'bg-slate-400';
    const percentage = (gasto / orcado) * 100;
    if (percentage > 100) return 'bg-red-500';
    if (percentage > 80) return 'bg-amber-500';
    return 'bg-green-500';
};
const getIconForGasto = (gasto) => {
    const desc = (gasto.descricao + (gasto.categoria || '')).toLowerCase();
    if (desc.includes('obra') || desc.includes('pedreiro') || desc.includes('pintor') || desc.includes('eletricista')) return 'engineering';
    if (desc.includes('material') || desc.includes('cimento') || desc.includes('tinta') || desc.includes('fio')) return 'construction';
    if (desc.includes('comida') || desc.includes('almoço') || desc.includes('alimentação')) return 'restaurant';
    if (desc.includes('transporte') || desc.includes('gasolina') || desc.includes('uber')) return 'local_shipping';
    if (desc.includes('ferramenta')) return 'plumbing';
    return 'shopping_cart';
};

const selectOrcamento = async (orcamento) => {
    isLoading.value = true;
    try {
        const response = await axios.get(`${API_BASE_URL}/orcamentos-projeto/${orcamento.uuid}`, authConfig());
        selectedOrcamento.value = response.data;
        currentView.value = 'details';
        if (route.params.id !== orcamento.uuid) {
            router.push({ name: 'OrcamentoProjeto', params: { id: orcamento.uuid } });
        }
    } catch (error) {
        showNotification('error', "Não foi possível carregar os detalhes do orçamento.");
        router.push({ name: 'OrcamentoProjeto' });
    } finally { isLoading.value = false; }
};

const returnToList = async () => {
    currentView.value = 'list';
    selectedOrcamento.value = null;
    router.push({ name: 'OrcamentoProjeto', params: { id: undefined } });
    await fetchOrcamentos();
};

const fetchOrcamentos = async () => {
    isLoading.value = true;
    try {
        const response = await axios.get(`${API_BASE_URL}/orcamentos-projeto`, authConfig());
        orcamentos.value = response.data.orcamentosAtivos || [];
        convitesPendentes.value = response.data.convitesPendentes || [];
    } catch (error) { showNotification('error', "Não foi possível carregar os orçamentos."); }
    finally { isLoading.value = false; }
};
const fetchCurrentUser = () => {
    const userId = localStorage.getItem('usuario_id');
    if (userId) { currentUser.value = { id: parseInt(userId, 10) }; }
};
const refreshSelectedOrcamento = async () => {
    if (!selectedOrcamento.value?.uuid) return;
    try {
        const response = await axios.get(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}`, authConfig());
        selectedOrcamento.value = response.data;
    } catch (e) {
        showNotification('error', "Erro ao atualizar dados do orçamento.");
        returnToList();
    }
};
const handleAccept = async (conviteId) => {
    try {
        await axios.post(`${API_BASE_URL}/convites/${conviteId}/aceitar`, {}, authConfig());
        showNotification('success', "Convite aceito com sucesso!");
        fetchOrcamentos();
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao aceitar convite."); }
};
const handleDecline = async (conviteId) => {
    try {
        await axios.post(`${API_BASE_URL}/convites/${conviteId}/recusar`, {}, authConfig());
        showNotification('success', "Convite recusado.");
        convitesPendentes.value = convitesPendentes.value.filter(c => c.conviteId !== conviteId);
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao recusar convite."); }
};

const openCreateOrcamentoModal = () => {
    newOrcamento.value = { nome: '', descricao: '', valorOrcado: null, emailsMembros: '' };
    showCreateModal.value = true;
};

const handleCreateOrcamento = async () => {
    isSubmitting.value = true;
    try {
        const payload = { ...newOrcamento.value, emailsMembros: newOrcamento.value.emailsMembros.split(/[\s,]+/).filter(Boolean) };
        const response = await axios.post(`${API_BASE_URL}/orcamentos-projeto`, payload, authConfig());
        showCreateModal.value = false;
        newOrcamento.value = { nome: '', descricao: '', valorOrcado: null, emailsMembros: '' };
        await fetchOrcamentos();
        if (response.data?.uuid) {
            await selectOrcamento({ uuid: response.data.uuid });
        } else {
            await returnToList();
        }
        showNotification('success', "Orçamento criado com sucesso!");
    } catch (error) { showNotification('error', "Erro ao criar orçamento."); }
    finally { isSubmitting.value = false; }
};

const openEditOrcamentoModal = () => {
    if (!selectedOrcamento.value) return;
    editOrcamentoForm.value = { nome: selectedOrcamento.value.nome, descricao: selectedOrcamento.value.descricao, valorOrcado: selectedOrcamento.value.valorOrcado };
    showEditOrcamentoModal.value = true;
};

const handleUpdateOrcamento = async () => {
    isSubmitting.value = true;
    try {
        await axios.put(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}`, editOrcamentoForm.value, authConfig());
        await refreshSelectedOrcamento();
        showEditOrcamentoModal.value = false;
        showNotification('success', "Orçamento atualizado com sucesso!");
    } catch (error) { showNotification('error', "Erro ao atualizar orçamento."); }
    finally { isSubmitting.value = false; }
};

const confirmDeleteOrcamento = () => { showConfirmDeleteOrcamento.value = true; };
const executeDeleteOrcamento = async () => {
    showConfirmDeleteOrcamento.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}`, authConfig());
        await returnToList();
        showNotification('success', "Orçamento excluído com sucesso.");
    } catch (error) { showNotification('error', "Erro ao excluir orçamento."); }
};
const handleInviteMember = async () => {
    isSubmitting.value = true;
    try {
        await axios.post(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}/convidar`, inviteForm.value, authConfig());
        await refreshSelectedOrcamento();
        showInviteModal.value = false;
        inviteForm.value.email = '';
        showNotification('success', "Membro convidado com sucesso!");
    } catch (error) { showNotification('error', error.response?.data?.message || "Não foi possível convidar o membro."); }
    finally { isSubmitting.value = false; }
};
const confirmRemoveMember = (membro) => { memberToRemove.value = membro; showConfirmRemoveMember.value = true; };
const executeRemoveMember = async () => {
    showConfirmRemoveMember.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}/membros/${memberToRemove.value.id}`, authConfig());
        await refreshSelectedOrcamento();
        showNotification('success', "Membro removido com sucesso.");
    } catch (error) { showNotification('error', "Erro ao remover membro."); }
    finally { memberToRemove.value = null; }
};
const confirmCancelInvite = (membro) => { inviteToCancel.value = membro; showConfirmCancelInvite.value = true; };
const executeCancelInvite = async () => {
    if (!inviteToCancel.value?.conviteId) return;
    showConfirmCancelInvite.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/convites/${inviteToCancel.value.conviteId}/cancelar`, authConfig());
        await refreshSelectedOrcamento();
        showNotification('success', 'Convite cancelado com sucesso.');
    } catch (error) { showNotification('error', error.response?.data?.message || "Erro ao cancelar o convite."); }
    finally { inviteToCancel.value = null; }
};
const confirmLeaveOrcamento = () => { showConfirmLeave.value = true; };
const executeLeaveOrcamento = async () => {
    showConfirmLeave.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}/membros/leave`, authConfig());
        await returnToList();
        showNotification('success', "Você saiu do projeto.");
    } catch (error) { showNotification('error', "Erro ao sair do projeto."); }
};

const openAddGastoModal = () => {
    gastoForm.value = { descricao: '', valor: null, categoria: '', data: new Date().toISOString().split('T')[0] }; // Reset form
    showAddGastoModal.value = true;
};

const handleAddGasto = async () => {
    isSubmitting.value = true;
    try {
        await axios.post(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}/gastos`, gastoForm.value, authConfig());
        await refreshSelectedOrcamento();
        showAddGastoModal.value = false;
        gastoForm.value = { descricao: '', valor: null, categoria: '', data: new Date().toISOString().split('T')[0] };
        showNotification('success', "Gasto adicionado com sucesso!");
    } catch (error) { showNotification('error', "Erro ao adicionar gasto."); }
    finally { isSubmitting.value = false; }
};
const openEditGastoModal = (gasto) => {
    gastoToEdit.value = gasto;
    editGastoForm.value = { ...gasto };
    showEditGastoModal.value = true;
};
const handleUpdateGasto = async () => {
    if (!gastoToEdit.value) return;
    isSubmitting.value = true;
    try {
        await axios.put(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}/gastos/${gastoToEdit.value.id}`, editGastoForm.value, authConfig());
        await refreshSelectedOrcamento();
        showEditGastoModal.value = false;
        showNotification('success', "Gasto atualizado com sucesso!");
    } catch (error) { showNotification('error', "Erro ao atualizar gasto."); }
    finally { isSubmitting.value = false; }
};
const confirmDeleteGasto = (gasto) => { gastoToDelete.value = gasto; showConfirmDeleteGasto.value = true; };
const executeDeleteGasto = async () => {
    if (!gastoToDelete.value) return;
    showConfirmDeleteGasto.value = false;
    try {
        await axios.delete(`${API_BASE_URL}/orcamentos-projeto/${selectedOrcamento.value.uuid}/gastos/${gastoToDelete.value.id}`, authConfig());
        await refreshSelectedOrcamento();
        showNotification('success', "Gasto removido com sucesso.");
    } catch (error) { showNotification('error', "Erro ao remover gasto."); }
    finally { gastoToDelete.value = null; }
};

onMounted(() => {
    fetchCurrentUser();
    const orcamentoId = route.params.id;
    if (orcamentoId) {
        selectOrcamento({ uuid: orcamentoId });
    } else {
        fetchOrcamentos();
        currentView.value = 'list';
    }
    checkDevice();
    window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
    window.removeEventListener('resize', checkDevice);
    if (notificationTimer.value) clearTimeout(notificationTimer.value);
});
</script>

<style scoped>
.form-label {
    @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1;
}

.form-input {
    @apply w-full px-3 py-2 text-sm bg-white dark:bg-slate-700/60 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-opacity-70 focus:border-brand-500 text-slate-800 dark:text-slate-200;
}

.btn-primary {
    @apply px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed;
}

.btn-secondary {
    @apply px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 dark:text-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-danger {
    @apply px-5 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors duration-150 ease-in-out;
}

.btn-danger-outline {
    @apply px-5 py-2.5 text-sm font-medium text-red-600 bg-transparent border border-red-500 hover:bg-red-50 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-action-icon {
    @apply p-1.5 rounded-full transition-colors duration-200;
}

.card-budget {
    @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg flex flex-col justify-between transition-all duration-300 hover:shadow-xl dark:hover:shadow-brand-700/30 cursor-pointer hover:-translate-y-1;
}

.empty-state {
    @apply text-center py-16 text-slate-500 dark:text-slate-400 bg-white dark:bg-brand-800 rounded-xl shadow-md flex flex-col items-center justify-center;
}

.skeleton-card {
    @apply bg-white dark:bg-brand-800 p-6 rounded-2xl shadow-lg animate-pulse space-y-4;
}

.tag-owner {
    @apply ml-2 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-brand-700 dark:text-brand-500 px-2 py-0.5 rounded-full;
}

.tag-pending {
    @apply ml-2 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300 px-2 py-0.5 rounded-full;
}

.modal-backdrop {
    @apply fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 dark:bg-brand-900/70 backdrop-blur-sm p-4;
}

.modal-container {
    @apply bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg transform transition-all;
}

.modal-header {
    @apply px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-close-btn {
    @apply p-1 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors;
}

.modal-footer {
    @apply p-4 bg-slate-50 dark:bg-slate-700/50 flex justify-end space-x-3 rounded-b-xl;
}

.modal-title {
    @apply mt-2 text-xl font-semibold text-slate-800 dark:text-slate-100;
}

.modal-text {
    @apply mt-2 text-sm text-slate-600 dark:text-slate-400;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
}

.animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.view-fade-enter-active,
.view-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.view-fade-enter-from {
    opacity: 0;
    transform: translateX(-10px);
}

.view-fade-leave-to {
    opacity: 0;
    transform: translateX(10px);
}

.list-enter-active,
.list-leave-active {
    transition: all 0.4s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateY(20px);
}

.timeline-container {
    @apply border-l-2 border-slate-200 dark:border-slate-700;
}

.timeline-item {
    @apply relative pl-8 py-4;
}

.timeline-point {
    @apply bg-green-500 absolute -left-[9px] top-[28px] w-4 h-4 rounded-full border-4 border-white dark:border-brand-800;
}

.timeline-content {
    @apply ml-4;
}

.notification-enter-active,
.notification-leave-active {
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.notification-enter-from,
.notification-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.animate-pulse-fast {
    animation: pulse-fast 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse-fast {
    50% {
        opacity: .85;
    }
}
</style>